import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';
import NotificationPanel from './NotificationPanel';
import { getAssetUrl } from '../utils/assets';
import './Layout.css';

const MOBILE_BREAKPOINT = 1024;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const allMenuItems: Array<{
    label: string;
    icon: string;
    path: string;
    badge: string | null;
    permission?: Permission;
  }> = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      path: '/dashboard',
      badge: null,
      permission: Permission.VIEW_DASHBOARD
    },
    {
      label: 'Tickets',
      icon: 'pi pi-ticket',
      path: '/tickets',
      badge: null,
      permission: Permission.VIEW_OWN_TICKETS
    },
    {
      label: 'Projects',
      icon: 'pi pi-th-large',
      path: '/projects',
      badge: null,
      permission: Permission.VIEW_PROJECTS
    },
    {
      label: 'New Ticket',
      icon: 'pi pi-plus-circle',
      path: '/tickets/new',
      badge: null,
      permission: Permission.CREATE_TICKET
    },
    {
      label: 'Companies',
      icon: 'pi pi-building',
      path: '/companies',
      badge: null,
      permission: Permission.VIEW_COMPANIES
    },
    {
      label: 'Users',
      icon: 'pi pi-users',
      path: '/users',
      badge: null,
      permission: Permission.VIEW_USERS
    },
    {
      label: 'Audit log',
      icon: 'pi pi-list',
      path: '/audit-logs',
      badge: null,
      permission: Permission.VIEW_AUDIT_LOGS
    },
    {
      label: 'Time Tracking',
      icon: 'pi pi-clock',
      path: '/time-tracking',
      badge: null,
      permission: Permission.VIEW_TIME_TRACKING
    },
    {
      label: 'Team usage',
      icon: 'pi pi-chart-line',
      path: '/usage',
      badge: null,
      permission: Permission.VIEW_COMPANY_USAGE
    },
    {
      label: 'Invoices',
      icon: 'pi pi-file',
      path: '/invoices',
      badge: null,
      permission: Permission.VIEW_BILLING
    },
    {
      label: 'New Invoice',
      icon: 'pi pi-plus',
      path: '/invoices/new',
      badge: null,
      permission: Permission.MANAGE_BILLING
    },
    {
      label: 'Deals',
      icon: 'pi pi-briefcase',
      path: '/deals',
      badge: null,
      permission: Permission.VIEW_DEALS
    },
    {
      label: 'Profile',
      icon: 'pi pi-user',
      path: '/profile',
      badge: null
    }
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => !item.permission || can(item.permission));

  useEffect(() => {
    const syncViewport = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('mobile-menu-open', isMobile && mobileMenuOpen);
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobile, mobileMenuOpen]);

  const isActiveRoute = (path: string) => {
    if (path === '/tickets') {
      return location.pathname === '/tickets' || /^\/tickets\/(?!new$)[^/]+$/.test(location.pathname);
    }
    if (path === '/projects') {
      return location.pathname === '/projects' || location.pathname.startsWith('/projects/');
    }
    if (path === '/invoices') {
      return location.pathname === '/invoices';
    }
    if (path === '/invoices/new') {
      return location.pathname === '/invoices/new';
    }
    return location.pathname === path;
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const sidebarHidden = isMobile ? !mobileMenuOpen : sidebarCollapsed;
  const brandLogo = getAssetUrl(user?.company?.logo);
  const brandIcon = getAssetUrl(user?.company?.icon) || brandLogo;
  const brandName = user?.company?.name || 'Uptimio';

  return (
    <div className={`modern-layout ${isMobile ? 'is-mobile' : ''}`}>
      {isMobile && mobileMenuOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Close menu"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <aside className={`sidebar ${sidebarHidden ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${isMobile && mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Logo Section */}
        <div className="sidebar-header">
          <div className="logo-container">
            {sidebarHidden && !isMobile ? (
              <img
                src={brandIcon || '/UptimioIcon.png'}
                alt={brandName}
                className="logo-icon-image"
              />
            ) : brandLogo ? (
              <img
                src={brandLogo}
                alt={brandName}
                className="logo-image"
              />
            ) : brandIcon ? (
              <div className="logo-text-brand">
                <img
                  src={brandIcon}
                  alt={brandName}
                  className="logo-icon-image"
                />
                <span>{brandName}</span>
              </div>
            ) : (
              <img
                src="/UptimioHelpdesk.png"
                alt="Uptimio Helpdesk"
                className="logo-image"
              />
            )}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => isMobile ? setMobileMenuOpen(false) : setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={isMobile ? 'Close menu' : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
          >
            <i className={`pi ${isMobile ? 'pi-times' : (sidebarCollapsed ? 'pi-angle-right' : 'pi-angle-left')}`}></i>
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => (
            <div
              key={index}
              className={`nav-item ${isActiveRoute(item.path) ? 'active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              <i className={item.icon}></i>
              {!sidebarHidden && (
                <>
                  <span className="nav-label">{item.label}</span>
                  {item.badge && (
                    <Badge value={item.badge} severity="danger" />
                  )}
                </>
              )}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="sidebar-footer">
          <div className="user-profile" onClick={() => handleNavigate('/profile')} role="button">
            {getAssetUrl(user?.avatar) ? (
              <img
                src={getAssetUrl(user?.avatar)}
                alt={`${user?.firstName} ${user?.lastName}`}
                className="user-avatar-image"
              />
            ) : (
              <Avatar
                label={user?.firstName?.charAt(0)}
                shape="circle"
                className="user-avatar"
              />
            )}
            {!sidebarHidden && (
              <div className="user-info">
                <div className="user-name">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="user-role">
                  <span className={`role-badge role-${user?.role?.toLowerCase()}`}>
                    {user?.role}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {!sidebarHidden && (
            <>
              <button className="logout-btn" onClick={logout}>
                <i className="pi pi-sign-out"></i>
                <span>Sign out</span>
              </button>
              <div className="powered-by">Powered by Uptimio</div>
            </>
          )}
          
          {sidebarHidden && !isMobile && (
            <>
              <button className="logout-btn-collapsed" onClick={logout}>
                <i className="pi pi-sign-out"></i>
              </button>
              <div className="powered-by powered-by-collapsed">Uptimio</div>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${!isMobile && sidebarCollapsed ? 'expanded' : ''} ${isMobile ? 'mobile' : ''}`}>
        <header className="top-bar">
          <div className="top-bar-left">
            {isMobile && (
              <button
                type="button"
                className="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <i className="pi pi-bars"></i>
              </button>
            )}
            <div className="breadcrumb">
              <i className="pi pi-home"></i>
              <span className="breadcrumb-sep">/</span>
              <span className="current-page">
                {menuItems.find(item => item.path === location.pathname)?.label ||
                 (location.pathname === '/profile' ? 'Profile' :
                 location.pathname.startsWith('/tickets/') ? 'Ticket' :
                 location.pathname.startsWith('/projects') ? 'Projects' :
                 location.pathname.startsWith('/audit-logs') ? 'Audit log' :
                 'Dashboard')}
              </span>
            </div>
          </div>
          <div className="top-bar-actions">
            <NotificationPanel />
            <button className="action-btn" onClick={() => navigate('/profile')} title="Profile">
              <i className="pi pi-cog"></i>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout; 